import { readableTextColor } from '../../lib/color';
import type { ScheduleCellAssignment } from '../../lib/types';

interface ScheduleBarProps {
  assignment: ScheduleCellAssignment;
  /** Assignment starts before the visible window — square off the left edge. */
  continuesBefore: boolean;
  /** Assignment ends after the visible window — square off the right edge. */
  continuesAfter: boolean;
  onClick: () => void;
}

/**
 * One assignment as a continuous bar spanning its planned days.
 *
 * The label **wraps** rather than being split into fixed per-field lines: a wide bar puts the
 * job number, project and location on one line and gets a roomy single row; a narrow one flows
 * onto the second. Nothing is truncated per-field, so a short project name never wastes the
 * line a long location needed. `line-clamp-2` is the only limit, and it exists because the row
 * height is computed up front for the virtualizer — text may not push the bar taller.
 *
 * Every project gets its own server-generated colour, so the fill is arbitrary and the text
 * colour flips against it (see {@link readableTextColor}). A bar clipped by the window edge
 * loses its rounding on that side, so a run that continues off-screen looks cut rather than
 * finished — the difference between "this job ends Friday" and "this is all you can see".
 */
export function ScheduleBar({
  assignment,
  continuesBefore,
  continuesAfter,
  onClick,
}: ScheduleBarProps) {
  const { colorHex, isPtoEquivalent, jobNumber, projectName, categoryName, account } = assignment;
  const isUncategorized = categoryName === 'Uncategorized';

  const style: React.CSSProperties = {
    backgroundColor: colorHex,
    color: readableTextColor(colorHex),
    borderTopLeftRadius: continuesBefore ? 0 : undefined,
    borderBottomLeftRadius: continuesBefore ? 0 : undefined,
    borderTopRightRadius: continuesAfter ? 0 : undefined,
    borderBottomRightRadius: continuesAfter ? 0 : undefined,
  };

  if (isPtoEquivalent) {
    style.backgroundImage =
      'repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 4px)';
  }

  const label = [
    `${jobNumber} ${projectName}`,
    account,
    `${assignment.plannedStart} to ${assignment.plannedEnd}`,
    isPtoEquivalent ? 'PTO-equivalent' : null,
    isUncategorized ? 'Uncategorized' : null,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      type="button"
      role="gridcell"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={style}
      className="h-full w-full rounded px-2 py-0.5 text-left overflow-hidden flex items-center focus:outline-none focus:ring-2 focus:ring-brand-blue/60"
    >
      {/* overflow-wrap:anywhere, not break-words: a long unbroken job code or site name has no
          space to break at, and without it the word overflows the bar instead of wrapping. */}
      <span className="line-clamp-2 text-xs leading-[1.35] [overflow-wrap:anywhere]">
        <span className="font-mono text-[11px] opacity-90">{jobNumber}</span>{' '}
        <span className="font-semibold">{projectName}</span>
        {account && <span className="opacity-90"> · {account}</span>}
      </span>
    </button>
  );
}
